import React from 'react';

import { useDraggable, useDroppable } from '@dnd-kit/core';

import { Box } from '@mui/material';

import type { ProjectAssignee, ProjectTask } from '~/services/ContractingService';

import TaskCard from './TaskCard';

interface Props {
  dropIndicator?: 'top' | 'bottom';
  suggestedAssignees: Array<ProjectAssignee>;
  task: ProjectTask;
  onChangeAssignees: (taskId: string, assignees: Array<ProjectAssignee>) => void;
}

export default function DraggableTaskCard({ dropIndicator, suggestedAssignees, task, onChangeAssignees }: Props) {
  const { attributes, listeners, setNodeRef: setDragRef, isDragging } = useDraggable({ id: task.id });
  const { setNodeRef: setDropRef } = useDroppable({ id: task.id });

  return (
    <Box
      ref={(node: HTMLElement | null) => {
        setDragRef(node);
        setDropRef(node);
      }}
      {...attributes}
      aria-roledescription="draggable task"
      role="group"
      // Popover events bubble through its portal and button keys bubble here, so only drag from the card itself.
      onPointerDown={(event) => {
        if (event.currentTarget.contains(event.target as Node)) {
          listeners?.onPointerDown(event);
        }
      }}
      onKeyDown={(event) => {
        if (event.target === event.currentTarget) {
          listeners?.onKeyDown(event);
        }
      }}
      sx={{
        borderRadius: 1,
        cursor: 'grab',
        opacity: isDragging ? 0.4 : 1,
        position: 'relative',
        '&:focus-visible': { outline: 2, outlineColor: 'primary.main', outlineStyle: 'solid' },
        '&::after': dropIndicator && {
          bgcolor: 'primary.main',
          borderRadius: 1,
          content: '""',
          height: 2,
          left: 0,
          position: 'absolute',
          right: 0,
          [dropIndicator]: -5,
        },
      }}
    >
      <TaskCard suggestedAssignees={suggestedAssignees} task={task} onChangeAssignees={onChangeAssignees} />
    </Box>
  );
}
